import { AutotractorPropsType } from "common/components/map/layers/types";
import { BuildFarmName } from "./wizard-steps/2BuildFarmName";
import { BuildFieldName } from "./wizard-steps/3BuildFieldName";
import { buildPermission, getCustomerId } from "common/utils/auth";
import {
  Button,
  LinearProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { BUTTON, classes } from "portal/theme/theme";
import { capitalize } from "common/utils/strings";
import { ChooseDataToImport } from "./wizard-steps/4ChooseDataToImport";
import { ConvertedFarm } from "./helpers";
import { Farm, PointDefinition } from "protos/farm/farm";
import { farmApi } from "common/state/portalApi/farm";
import { FarmMap } from "../FarmMap";
import { farmToFarmMapData } from "common/hooks/useFarmMapData";
import { FeatureCollectionWithFilename } from "shpjs";
import {
  GeojsonToProtoConvertRequest,
  ShapefileImportWorkerResponse,
} from "./workerApi";
import { Header } from "portal/components/header/Header";
import { Page } from "portal/components/Page";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { Point } from "protos/geo/geo";
import { portalLanguageOrDefault } from "portal/i18nConstants";
import { PreviewAndFinish } from "./wizard-steps/5PreviewAndFinish";
import { SelectFile } from "./wizard-steps/1SelectFile";
import {
  ShapefileBundleWizardProps,
  ShapefileLoadingState,
  WizardStep,
} from "./Wizard";
import { shapefileImportWorker } from "portal/workers";
import { UnauthorizedPage } from "portal/components/UnauthorizedPage";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { withAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import React, {
  FC,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

export const _ShapefileBundleImport: FunctionComponent = () => {
  const { t } = useTranslation();
  return (
    <>
      <Header
        title={capitalize(t("models.farms.farm_one"))}
        pageTitle={"import shapefile"}
        parentLink={"/farms"}
      />
      <Page maxWidth={false}>
        {/* like `<NoScroll>` but only for `md:` and above */}
        <div className="flex flex-col absolute inset-10 md:overflow-hidden">
          <ShapefileBundleImportView />
        </div>
      </Page>
    </>
  );
};

const ShapefileBundleImportView: FC = () => {
  const { i18n, t } = useTranslation();
  const locale = portalLanguageOrDefault(i18n.language);
  const listFormatter = new Intl.ListFormat(locale, {
    style: "narrow",
    type: "conjunction",
  });

  const { user } = useSelf();
  const [customerId, setCustomerId] = useState<number | undefined>(
    getCustomerId(user)
  );
  const navigate = useNavigate();

  const [shapefileLoadingState, setShapefileLoadingState] = useState(
    ShapefileLoadingState.NotStarted
  );
  const [shapefile, setShapefile] = useState<FeatureCollectionWithFilename[]>(
    []
  );

  const resetShapefile = useCallback(() => {
    setShapefile([]);
  }, []);

  const inputFeatures = useMemo(() => {
    // sometimes, shpjs gives us an undefined feature just to keep us on our
    // toes; do an explicit check even though this is excluded by the type
    return shapefile.flatMap((shp) => shp.features).filter(Boolean);
  }, [shapefile]);
  const [farmNameTemplate, setFarmNameTemplate] = useState<string[]>([]);
  const [fieldNameTemplate, setFieldNameTemplate] = useState<string[]>([]);
  const [busyConverting, setBusyConverting] = useState<boolean>(false);
  const [convertedFarms, setConvertedFarms] = useState<ConvertedFarm[]>([]);

  const propertyExamples = useMemo(() => {
    const examples: Map<string, Set<string>> = new Map();

    for (const cvtFarm of convertedFarms) {
      for (const cvtField of cvtFarm.fields) {
        if (cvtField.canImport && cvtField.feature.properties) {
          for (const [propName, propValue] of Object.entries(
            cvtField.feature.properties
          )) {
            const exemplarsThisName = examples.get(propName) ?? new Set();
            exemplarsThisName.add(propValue.toString());
            examples.set(propName, exemplarsThisName);
          }
        }
      }
    }

    return examples;
  }, [convertedFarms]);

  const nImportableFields = useMemo(() => {
    let n = 0;

    for (const cvtFarm of convertedFarms) {
      n += cvtFarm.fields.filter((field) => field.canImport).length;
    }

    return n;
  }, [convertedFarms]);
  const farmsMapData = useMemo(
    () => convertedFarms.map((cvtFarm) => farmToFarmMapData(cvtFarm.farm)),
    [convertedFarms]
  );
  const [selectedZoneIds, setSelectedZoneIds] = useState(new Set<string>());
  const [focusedZoneIds, setFocusedZoneIds] = useState<string[]>([]);
  /**
   * selectedFarmIds is the set of all farms which have more than zero
   * importable fields and all of them are selected for import, for updating
   * the state of the farm-level checkboxes.
   *
   * farmsForImport is the current farm protos to be imported into Ops Center,
   * with deselected fields removed etc.
   */
  const computedSelection = useMemo(() => {
    const selectedFarmIds = new Set<string>();
    const farmsForImport: Farm[] = [];

    for (const cvtFarm of convertedFarms) {
      // we need to rebuild the lists of zones and pointDefs, only including the
      // zones which are selected for import and the pointDefs they reference
      const thisFarmForImport: Farm = {
        ...cvtFarm.farm,
        pointDefs: [],
        zones: [],
      };
      const pointDefsById = new Map<string, PointDefinition>(
        cvtFarm.farm.pointDefs
          .filter((ptdef) => ptdef.point?.id)
          .map((ptdef) => [ptdef.point!.id!.id, ptdef])
      );
      let allImportableFieldsImported = true;

      // eslint-disable-next-line no-inner-declarations
      function addPointDef(point: Point): void {
        const pointDef = point.id?.id
          ? pointDefsById.get(point.id.id)
          : undefined;

        if (pointDef) {
          thisFarmForImport.pointDefs.push(pointDef);
        } else {
          throw new Error(
            `computedSelection.addPointDef: point def doesn't exist for point id ${point.id?.id}?`
          );
        }
      }

      for (const cvtField of cvtFarm.fields) {
        if (cvtField.canImport) {
          if (selectedZoneIds.has(cvtField.zoneId)) {
            // copy the zone into thisFarmToImport from cvtFarm
            const zone = cvtFarm.farm.zones.find(
              (z) => z.id?.id === cvtField.zoneId
            );

            if (zone) {
              const importableZone = structuredClone(zone);

              for (const area of importableZone.areas) {
                if (area.point) {
                  addPointDef(area.point);
                } else if (area.lineString) {
                  for (const pt of area.lineString.points) {
                    addPointDef(pt);
                  }
                } else if (area.polygon) {
                  for (const pt of area.polygon.boundary?.points ?? []) {
                    addPointDef(pt);
                  }

                  for (const hole of area.polygon.holes) {
                    for (const pt of hole.points) {
                      addPointDef(pt);
                    }
                  }
                }
              }

              thisFarmForImport.zones.push(importableZone);
            }
          } else {
            allImportableFieldsImported = false;
          }
        }
      }

      if (thisFarmForImport.zones.length > 0) {
        if (allImportableFieldsImported) {
          selectedFarmIds.add(cvtFarm.farmId);
        }

        farmsForImport.push(thisFarmForImport);
      }
    }

    return {
      selectedFarmIds,
      farmsForImport,
    };
  }, [convertedFarms, selectedZoneIds]);

  const displaySelectedGeoIds = useMemo(
    () => [...selectedZoneIds, ...computedSelection.selectedFarmIds],
    [selectedZoneIds, computedSelection.selectedFarmIds]
  );

  /**
   * returns the list of zone IDs that should be (de)selected when a given geo
   * ID is selected.  for fields, this is just the field's zone ID; for farms,
   * this is all the zone IDs for the farm's importable fields.
   */
  const associatedZoneIds = useCallback(
    (geoId: string) => {
      for (const cvtFarm of convertedFarms) {
        if (cvtFarm.farmId === geoId) {
          return new Set(
            cvtFarm.fields
              .filter((cvtField) => cvtField.canImport)
              .map((cvtField) => cvtField.zoneId)
          );
        } else {
          for (const cvtField of cvtFarm.fields) {
            if (cvtField.zoneId === geoId) {
              return new Set([cvtField.zoneId]);
            }
          }
        }
      }

      return new Set([]);
    },
    [convertedFarms]
  );

  const toggleSelection = useCallback(
    (geoId: string) => {
      const assocIdSet = associatedZoneIds(geoId);

      if (assocIdSet.isSubsetOf(selectedZoneIds)) {
        setSelectedZoneIds(selectedZoneIds.difference(assocIdSet));
      } else {
        setSelectedZoneIds(selectedZoneIds.union(assocIdSet));
      }
    },
    [associatedZoneIds, selectedZoneIds]
  );

  const resetSelection = useCallback(() => {
    setSelectedZoneIds(new Set());
  }, []);

  const focusGeoId = useCallback(
    (geoId: string) => {
      setFocusedZoneIds([...associatedZoneIds(geoId)]);
    },
    [associatedZoneIds]
  );

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const steps: WizardStep<ShapefileBundleWizardProps>[] = useMemo(
    () => [
      SelectFile,
      BuildFarmName,
      BuildFieldName,
      ChooseDataToImport,
      PreviewAndFinish,
    ],
    []
  );

  const currentStep = useMemo(
    () => steps[currentStepIndex],
    [steps, currentStepIndex]
  );

  const [createFarm] = useMutationPopups(
    farmApi.endpoints.createFarm.useMutation()
  );
  const [isUploading, setIsUploading] = useState(false);
  const [nFarmsUploaded, setNFarmsUploaded] = useState(0);
  const [farmNamesUploadFailed, setFarmNamesUploadFailed] = useState<string[]>(
    []
  );

  // offload loading shapefiles and converting geojson to farm protos to a web
  // worker to improve responsiveness
  useEffect(() => {
    function handleShpConvert(
      e: MessageEvent<ShapefileImportWorkerResponse>
    ): void {
      if (e.data.type === "shp-convert-resp") {
        if (e.data.error) {
          setShapefileLoadingState(ShapefileLoadingState.Error);
          console.error("shapefile load threw error:", e.data.error);
        } else {
          setShapefile(e.data.files);
          setShapefileLoadingState(ShapefileLoadingState.Ok);
        }

        // reset wizard state
        setFarmNameTemplate([]);
        setFieldNameTemplate([]);
        resetSelection();
      }
    }

    shapefileImportWorker.addEventListener("message", handleShpConvert);

    return () =>
      shapefileImportWorker.removeEventListener("message", handleShpConvert);
  }, [resetSelection]);

  useEffect(() => {
    const requestId = Date.now();

    function handleMessage(
      e: MessageEvent<ShapefileImportWorkerResponse>
    ): void {
      if (e.data.type === "proto-convert-resp") {
        if (e.data.requestId !== requestId) {
          // response is out-of-date; discard
          return;
        }

        if (e.data.error) {
          console.error("farm protos conversion threw error:", e.data.error);
          throw e.data.error;
        } else {
          setConvertedFarms(e.data.cvtFarms);
        }

        setBusyConverting(false);
      }
    }

    shapefileImportWorker.addEventListener("message", handleMessage);

    setBusyConverting(true);
    shapefileImportWorker.postMessage({
      type: "proto-convert-req",
      requestId,
      // if the customer id isn't set, use an extremely illegal sentinel value
      // so we can be sure the create farm request fails instead of silently
      // succeeding if it somehow gets that far
      customerId: customerId ?? -1,
      inputFeatures,
      farmNameTemplate,
      fieldNameTemplate,
    } satisfies GeojsonToProtoConvertRequest);

    return () =>
      shapefileImportWorker.removeEventListener("message", handleMessage);
  }, [
    customerId,
    farmNameTemplate,
    fieldNameTemplate,
    inputFeatures,
    resetSelection,
  ]);

  const onFinish = async (): Promise<void> => {
    const failedNames: string[] = [];
    setIsUploading(true);

    for (const farm of computedSelection.farmsForImport) {
      const result = await createFarm(farm);

      setNFarmsUploaded(nFarmsUploaded + 1);

      if (result.error) {
        failedNames.push(farm.name);
      }
    }

    setIsUploading(false);

    if (failedNames.length === 0) {
      navigate(Path.FARMS);
    } else {
      setFarmNamesUploadFailed(failedNames);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <Typography variant="h1" className="flex flex-row gap-2 text-4xl mb-3">
        {t("views.farms.importFarms.title")}
      </Typography>
      {/* the height here is set to account for the inset on the absolutely-positioned frame + padding */}
      <div className="flex flex-col md:flex-row h-[calc(100%-3rem)]">
        <Paper className="relative w-1/3 p-4 ml-auto overflow-y-auto">
          <div className="flex flex-col h-full gap-4">
            <Stepper alternativeLabel activeStep={currentStepIndex}>
              {steps.map((step, stepIndex) => (
                <Step key={stepIndex}>
                  <StepLabel>{t(step.stepNameI18NKey)}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {currentStep?.Render && (
              <currentStep.Render
                customerId={customerId}
                setCustomerId={setCustomerId}
                shapefile={shapefile}
                resetShapefile={resetShapefile}
                shapefileLoadingState={shapefileLoadingState}
                inputFeatures={inputFeatures}
                propertyExamples={propertyExamples}
                farmNameTemplate={farmNameTemplate}
                setFarmNameTemplate={setFarmNameTemplate}
                fieldNameTemplate={fieldNameTemplate}
                setFieldNameTemplate={setFieldNameTemplate}
                convertedFarms={convertedFarms}
                busyConverting={busyConverting}
                nImportableFields={nImportableFields}
                displaySelectedGeoIds={displaySelectedGeoIds}
                toggleSelection={toggleSelection}
                resetSelection={resetSelection}
                focusGeoId={focusGeoId}
                farmsForImport={computedSelection.farmsForImport}
              />
            )}
            {isUploading && (
              <LinearProgress
                variant="determinate"
                value={
                  (nFarmsUploaded / computedSelection.farmsForImport.length) *
                  100
                }
              />
            )}
            {farmNamesUploadFailed.length > 0 && (
              <div className="text-error">
                {t("views.farms.importFarms.uploadFailed", {
                  farmList: listFormatter.format(farmNamesUploadFailed),
                })}
              </div>
            )}
            <div className="flex flex-row gap-2">
              <Button
                {...BUTTON}
                className={classes(BUTTON.className, "flex-1")}
                disabled={currentStepIndex === 0}
                type="button"
                onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
              >
                {t("views.farms.importFarms.previousStep")}
              </Button>
              {currentStepIndex === steps.length - 1 ? (
                <Button
                  {...BUTTON}
                  className={classes(BUTTON.className, "flex-1")}
                  disabled={busyConverting}
                  type="button"
                  onClick={onFinish}
                >
                  {t("views.farms.importFarms.finish")}
                </Button>
              ) : (
                <Button
                  {...BUTTON}
                  className={classes(BUTTON.className, "flex-1")}
                  disabled={
                    busyConverting ||
                    !steps[currentStepIndex]?.canAdvancePastStep({
                      customerId,
                      setCustomerId,
                      shapefile,
                      resetShapefile,
                      shapefileLoadingState,
                      inputFeatures,
                      propertyExamples,
                      farmNameTemplate,
                      setFarmNameTemplate,
                      fieldNameTemplate,
                      setFieldNameTemplate,
                      convertedFarms,
                      busyConverting,
                      nImportableFields,
                      displaySelectedGeoIds,
                      toggleSelection,
                      resetSelection,
                      focusGeoId,
                      farmsForImport: computedSelection.farmsForImport,
                    })
                  }
                  type="button"
                  onClick={() => setCurrentStepIndex(currentStepIndex + 1)}
                >
                  {t("views.farms.importFarms.nextStep")}
                </Button>
              )}
            </div>
          </div>
        </Paper>
        {/* extra wrapper to prevent map from having a weird effective min-height */}
        <div className="farm-map-container relative flex min-h-64 md:min-h-0 flex-1">
          <FarmMap
            className={"absolute inset-0"}
            selectedPointId={undefined}
            farmsMapData={farmsMapData}
            onZoneHover={(f) => {
              if (currentStep?.mapSelectionEnabled) {
                if (f) {
                  const id = f.properties.zoneId;
                  if (id) {
                    focusGeoId(id);
                  }
                } else {
                  setFocusedZoneIds([]);
                }
              }
            }}
            onMapClick={(feature) => {
              if (
                currentStep?.mapSelectionEnabled &&
                feature?.properties.type === AutotractorPropsType.ZONE &&
                feature.properties.zoneId
              ) {
                toggleSelection(feature.properties.zoneId);
              }
            }}
            selectedZoneIds={displaySelectedGeoIds}
            focusedPointId={undefined}
            focusedZoneIds={focusedZoneIds}
          />
        </div>
      </div>
    </div>
  );
};

export const ShapefileBundleImport = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.update,
        PermissionResource.farms,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.update,
        PermissionResource.farms,
        PermissionDomain.all
      ),
    ],
  },
  _ShapefileBundleImport,
  <UnauthorizedPage />
);
